// Portfolio Administration v1.0 PR 5 — Vendors registry table.
//
// W7: Rating, Active assignments and YTD VFI are real, server-computed
// figures. They rendered hardcoded placeholders — an em-dash, and a null
// rating that read "Unrated" for every vendor — long after the data behind
// them existed, so a vendor with a live assignment, four invoices and $3,000
// paid still showed nothing. Tier and Territory are GONE: unlike the other
// three they had no data behind them at all (no column, no endpoint), and a
// column that can only ever show a placeholder is worse than no column.

import { useNavigate } from "react-router-dom";
import {
  Badge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import { formatCurrency } from "@/design-system/lib/format";
import { RegistryStatusPill } from "@/components/portfolioAdmin/registry/RegistryStatusPill";
import { RatingStars } from "@/components/portfolioAdmin/registry/RatingStars";

export interface VendorsListTableProps {
  vendors: VendorDto[];
}

export function VendorsListTable({ vendors }: VendorsListTableProps) {
  const navigate = useNavigate();
  if (vendors.length === 0) {
    return (
      <div
        className="text-small text-gray-mid italic px-space-3 py-space-4 text-center border border-border-light rounded-lg bg-white-pure"
        data-testid="vendors-empty-state"
      >
        No vendors match these filters yet.
      </div>
    );
  }
  return (
    <Table data-testid="vendors-list-table">
      <THead>
        <TR>
          <TH className="text-white-pure">Name</TH>
          <TH className="text-white-pure">Type</TH>
          <TH className="text-white-pure">Status</TH>
          <TH className="text-white-pure">Active assignments</TH>
          <TH className="text-white-pure">YTD VFI</TH>
          <TH className="text-white-pure">Rating</TH>
        </TR>
      </THead>
      <TBody>
        {vendors.map((v) => (
          <TR
            key={v.id}
            className="cursor-pointer"
            data-testid={`vendors-row-${v.id}`}
            onClick={() => navigate(`/portfolio-admin/vendors/${v.id}`)}
          >
            <TD>
              <span className="text-gold-shadow hover:underline font-semibold">
                {v.name}
              </span>
            </TD>
            <TD>{v.vendorType ?? "—"}</TD>
            <TD>
              <div className="flex items-center gap-space-1">
                <RegistryStatusPill status={v.status} />
                {v.isArchived ? (
                  <Badge
                    status="archived"
                    data-testid={`vendors-archived-pill-${v.id}`}
                  >
                    Archived
                  </Badge>
                ) : null}
              </div>
            </TD>
            <TD
              className={v.activeAssignmentCount ? "tabular-nums" : "text-gray-mid"}
              data-testid={`vendors-assignments-${v.id}`}
            >
              {v.activeAssignmentCount ?? 0}
            </TD>
            <TD className="tabular-nums" data-testid={`vendors-ytd-vfi-${v.id}`}>
              {formatCurrency(v.ytdVfi ?? 0)}
            </TD>
            <TD data-testid={`vendors-rating-${v.id}`}>
              <span className="flex items-baseline gap-space-1">
                <RatingStars rating={v.averageRating ?? null} />
                {/* A rating from one report is not the same claim as a rating
                    from thirty; the column should not present them alike. */}
                {(v.ratingCount ?? 0) > 0 && (
                  <span className="text-caption text-gray-mid">
                    ({v.ratingCount})
                  </span>
                )}
              </span>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
